import * as T from 'three';

/** Swept sphere contact in render-world coordinates. Correct position in place
 * and write the outward unit normal. No gameplay movement is performed here. */
export type DeathContactResolver=(from:Readonly<T.Vector3>,position:T.Vector3,radius:number,normal:T.Vector3)=>boolean;
export interface RagdollOptions {
  groundY?:number;
  resolveContact?:DeathContactResolver;
  seed?:number;
  impactWorld?:T.Vector3;
}
export const RAGDOLL_BONES=[
  'Hips','Spine2','Head','LeftArm','LeftForeArm','LeftHand','RightArm','RightForeArm','RightHand',
  'LeftUpLeg','LeftLeg','LeftFoot','RightUpLeg','RightLeg','RightFoot',
] as const;
export const RAGDOLL_FIT:readonly [string,number,number][]=[
  ['Hips',0,1],['Spine2',1,2],['LeftArm',3,4],['LeftForeArm',4,5],['RightArm',6,7],['RightForeArm',7,8],
  ['LeftUpLeg',9,10],['LeftLeg',10,11],['RightUpLeg',12,13],['RightLeg',13,14],
];
const EDGES:readonly [number,number][]=[[0,1],[1,2],[1,3],[3,4],[4,5],[1,6],[6,7],[7,8],[0,9],[9,10],[10,11],[0,12],[12,13],[13,14]];
const RADII=[.16,.19,.14,.095,.075,.07,.095,.075,.07,.105,.095,.105,.105,.095,.105];
const DT=1/120;
export interface DeathPoint {position:T.Vector3;previous:T.Vector3;velocity:T.Vector3;radius:number;normal:T.Vector3;}
interface DistanceConstraint {a:number;b:number;min:number;max:number;}

/** Local-space constrained body with a rigid torso, limited limb folding and
 * self separation. Fixed substeps preserve incoming momentum at 30–144 Hz. */
export class DeathRagdoll {
  readonly points:DeathPoint[];
  private readonly constraints:DistanceConstraint[]=[];
  private readonly separation:DistanceConstraint[]=[];
  private readonly delta=new T.Vector3();
  private readonly center=new T.Vector3();
  private readonly incoming=new T.Vector3();
  private readonly basis=new T.Matrix4();
  private readonly right=new T.Vector3();
  private readonly up=new T.Vector3();
  private readonly back=new T.Vector3();
  private readonly initialFrameInverse=new T.Quaternion();
  private accumulated=0;
  constructor(positions:readonly T.Vector3[],velocity:T.Vector3,impact=new T.Vector3(),seed=1){
    if(positions.length!==RAGDOLL_BONES.length)throw new Error('Ragdoll needs the complete joint set');
    const random=deathRandom(seed),scale=T.MathUtils.clamp(positions[0].distanceTo(positions[2])/.75,.7,1.3);
    for(const p of positions)this.center.add(p);this.center.divideScalar(positions.length);
    const spin=new T.Vector3(random()-.5,random()-.5,random()-.5).clampLength(0,.7);
    const kick=impact.clone().clampLength(0,3);
    this.points=positions.map((p,i)=>({position:p.clone(),previous:p.clone(),velocity:velocity.clone().add(kick).add(new T.Vector3().subVectors(p,this.center).cross(spin)),radius:RADII[i]*scale,normal:new T.Vector3()}));
    const linked=new Set<string>();
    const add=(a:number,b:number,min:number,max=min)=>{this.constraints.push({a,b,min,max});linked.add(`${Math.min(a,b)}:${Math.max(a,b)}`);};
    for(const [a,b]of EDGES)add(a,b,positions[a].distanceTo(positions[b]));
    // Cross-bracing the whole shoulder/hip/head frame keeps the torso from
    // collapsing into a ball, independent of the incoming animation pose.
    const torso=[0,1,2,3,6,9,12];
    for(let i=0;i<torso.length;i++)for(let j=i+1;j<torso.length;j++){
      const a=torso[i],b=torso[j];if(!linked.has(`${Math.min(a,b)}:${Math.max(a,b)}`))add(a,b,positions[a].distanceTo(positions[b]));
    }
    for(const [a,m,b,fraction]of [[3,4,5,.38],[6,7,8,.38],[9,10,11,.60],[12,13,14,.60]]){
      const length=positions[a].distanceTo(positions[m])+positions[m].distanceTo(positions[b]);
      add(a,b,Math.min(positions[a].distanceTo(positions[b]),length*fraction),length*.998);
    }
    for(let a=0;a<this.points.length;a++)for(let b=a+1;b<this.points.length;b++)if(!linked.has(`${a}:${b}`)){
      const min=Math.min((this.points[a].radius+this.points[b].radius)*1.05,positions[a].distanceTo(positions[b])*.96);
      this.separation.push({a,b,min,max:Infinity});
    }
    this.torsoFrame(this.initialFrameInverse).invert();
  }
  /** Full torso orientation includes twist; aiming only the spine axis leaves
   * shoulders facing the old direction while the physics body turns. */
  rotation(out:T.Quaternion):T.Quaternion{return this.torsoFrame(out).multiply(this.initialFrameInverse);}
  surfaceContact(correction:T.Vector3,normal:T.Vector3,dt:number):void{
    for(const point of this.points){
      point.position.add(correction);point.previous.add(correction);
      const inward=point.velocity.dot(normal);if(inward<0)point.velocity.addScaledVector(normal,-inward);
      point.velocity.multiplyScalar(Math.exp(-6*Math.min(.1,Math.max(0,dt))));
    }
  }
  private torsoFrame(out:T.Quaternion):T.Quaternion{
    this.right.subVectors(this.points[6].position,this.points[3].position).normalize();
    this.up.subVectors(this.points[2].position,this.points[0].position);
    this.up.addScaledVector(this.right,-this.up.dot(this.right)).normalize();
    this.back.crossVectors(this.right,this.up).normalize();this.up.crossVectors(this.back,this.right).normalize();
    return out.setFromRotationMatrix(this.basis.makeBasis(this.right,this.up,this.back));
  }
  step(dt:number,contact:DeathContactResolver):void{
    if(!Number.isFinite(dt)||dt<=0)return;
    this.accumulated+=Math.min(dt,.25);
    while(this.accumulated+1e-10>=DT){this.integrate(contact);this.accumulated-=DT;}
  }
  private constrain(c:DistanceConstraint):void{
    const a=this.points[c.a].position,b=this.points[c.b].position;
    this.delta.subVectors(b,a);const length=this.delta.length();
    const target=T.MathUtils.clamp(length,c.min,c.max);if(Math.abs(length-target)<1e-7)return;
    if(length<1e-8)this.delta.set(1,0,0);else this.delta.divideScalar(length);
    this.delta.multiplyScalar((length-target)*.5);a.add(this.delta);b.sub(this.delta);
  }
  private integrate(contact:DeathContactResolver):void{
    for(const point of this.points){
      point.previous.copy(point.position);point.normal.set(0,0,0);
      point.velocity.multiplyScalar(Math.exp(-.12*DT));point.velocity.y-=18*DT;
      point.position.addScaledVector(point.velocity,DT);
    }
    for(let iteration=0;iteration<9;iteration++){
      for(const constraint of this.constraints)this.constrain(constraint);
      for(const constraint of this.separation)this.constrain(constraint);
      // Three contact passes bracket groups of three internal constraint
      // iterations. World sweeps are far more expensive than joint constraints;
      // retain the rigid-body solve without querying the terrain nine times.
      if(iteration%3!==2)continue;
      for(const point of this.points){
        this.delta.set(0,0,0);
        if(contact(point.previous,point.position,point.radius,this.delta))point.normal.copy(this.delta);
      }
    }
    for(const point of this.points){
      this.incoming.copy(point.velocity);
      point.velocity.subVectors(point.position,point.previous).divideScalar(DT);
      if(point.normal.lengthSq()>.5){
        const inward=point.velocity.dot(point.normal);if(inward<0)point.velocity.addScaledVector(point.normal,-inward);
        const bounce=Math.min(.65,Math.max(0,-this.incoming.dot(point.normal)-1)*.05);
        point.velocity.multiplyScalar(Math.exp(-10*DT)).addScaledVector(point.normal,bounce);
      }
    }
  }
}

/** Converts the externally supplied world contact into the frozen avatar frame.
 * The compatibility ground plane remains useful for NPCs and isolated labs. */
export class DeathPhysicsSpace {
  private readonly fromWorld=new T.Vector3();
  private readonly toWorld=new T.Vector3();
  private readonly worldNormal=new T.Vector3();
  private readonly rotation=new T.Quaternion();
  private readonly inverse=new T.Matrix4();
  private readonly world=new T.Matrix4();
  private readonly worldPosition=new T.Vector3();
  private readonly worldScale=new T.Vector3();
  private radiusScale=1;
  constructor(private readonly root:T.Object3D,private readonly options:RagdollOptions={}){this.prepare();}
  /** The captured root is constant throughout one body's physics step, including
   * its wing debris. Refresh once when the planet/frame moves, not per contact. */
  prepare():void{
    this.root.updateWorldMatrix(true,false);this.world.copy(this.root.matrixWorld);
    this.inverse.copy(this.world).invert();
    this.world.decompose(this.worldPosition,this.rotation,this.worldScale);this.rotation.invert();
    this.radiusScale=Math.max(this.worldScale.x,this.worldScale.y,this.worldScale.z);
  }
  readonly resolve:DeathContactResolver=(from,position,radius,normal)=>{
    if(this.options.resolveContact){
      this.fromWorld.copy(from).applyMatrix4(this.world);this.toWorld.copy(position).applyMatrix4(this.world);
      if(!this.options.resolveContact(this.fromWorld,this.toWorld,radius*this.radiusScale,this.worldNormal))return false;
      position.copy(this.toWorld).applyMatrix4(this.inverse);
      normal.copy(this.worldNormal).applyQuaternion(this.rotation).normalize();return true;
    }
    const ground=this.options.groundY??0;
    if(position.y-radius<ground){position.y=ground+radius;normal.set(0,1,0);return true;}return false;
  };
}

export function deathRandom(seed:number):()=>number{
  let value=(seed|0)||1;return()=>{value^=value<<13;value^=value>>>17;value^=value<<5;return(value>>>0)/4294967296;};
}
